import { useEffect } from 'react';
import mixpanel from 'mixpanel-browser';

import { useAccount, useBundleId } from '@hcs/auth';
import { useComponentDidMount } from '@hcs/hooks';
import { MixpanelEventData } from '@hcs/types';
import { ProductionBundles } from '@hcs/types';
import { ENV } from '@hcs/webapps';

import { MIXPANEL_KEY } from '../constants/engagementTracking.constants';

// A hook for mixpanel user profile setup
// Exposes tracking methods to send events to mixpanel
// This hook shouldn't typically be used directly.
// Use useTrackMixpanelEvent instead to auto-populate events with data
export const useMixpanel = () => {
  const bundleId = useBundleId();
  const accountQuery = useAccount();
  const user = accountQuery.data?.user;
  const org = accountQuery.data?.currentOrganization;
  // Init Mixpanel onMount
  useComponentDidMount(() => {
    mixpanel.init(MIXPANEL_KEY || '');
  });
  // Login/Logout effect
  useEffect(() => {
    if (user) {
      const now = new Date().toISOString();
      const superProperties: Record<string, string | number | undefined> = {
        $email: user.email,
        $last_login: now,
        $organization_slug: org?.slug,
        $organization_id: org?.id,
      };
      // Ignore events from HC users in solutions-platform prod
      if (
        bundleId === ProductionBundles.SolutionsPlatform &&
        ENV === 'prod' &&
        user?.email
      ) {
        if (/(@housecanary.com)/i.test(user.email)) {
          superProperties['$ignore'] = 'true'; // disable tracking
        }
      }
      // Identify the user in mixpanel
      // and set user profile fields
      mixpanel.alias(user?.email);
      mixpanel.identify(user?.email);
      mixpanel.people.set({
        $first_name: user?.firstName,
        $last_name: user?.lastName,
        $name: `${user?.firstName} ${user?.lastName}`,
        $email: user?.email,
        $last_login: now,
        $organization_slug: org?.slug,
        $organization_id: org?.id,
      });
      mixpanel.register(superProperties);
    } else {
      // Reset mixpanel when user logs out
      mixpanel.reset();
    }
  }, [user, bundleId, org]);
  // Expose tracking methods
  return {
    track: (eventName: string, eventData: MixpanelEventData) => {
      mixpanel.track(eventName, eventData);
    },
    incReportsPulled: () => {
      mixpanel.people.increment('Value Reports Pulled', 1);
    },
  };
};
